import { Routes } from '@angular/router';
import { authGuard } from '../guards/auth.guard';
import { guestGuard } from '../guards/guest.guard';
import { kycDeactivateGuard } from '../guards/kyc-deactivate.guard';
import { kycOnboardingGuard } from '../guards/kyc-onboarding.guard';

export const routes: Routes = [
  {
    path: 'auth',
    loadComponent: () =>
      import('../layouts/auth-layout/auth-layout').then((m) => m.AuthLayout),
    children: [
      {
        path: 'login',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('../pages/auth/login/login').then((m) => m.Login),
      },
      {
        path: 'sign-up',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('../pages/auth/signup/signup').then((m) => m.Signup),
      },
      {
        path: 'verify-otp',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('../pages/auth/otp/otp').then((m) => m.Otp),
      },
      {
        path: 'forgot-password',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('../pages/auth/forgot-password/forgot-password').then((m) => m.ForgotPassword),
      },
      {
        path: 'reset-password-otp',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('../pages/auth/reset-password-otp/reset-password-otp').then((m) => m.ResetPasswordOtp),
      },
      {
        path: 'reset-password',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('../pages/auth/reset-password/reset-password').then((m) => m.ResetPassword),
      },
      {
        path: 'kyc',
        canActivate: [authGuard, kycOnboardingGuard],
        canDeactivate: [kycDeactivateGuard],
        loadComponent: () =>
          import('../pages/auth/kyc/kyc').then((m) => m.Kyc),
      },
    ],
  },
  {
    path: 'main',
    loadComponent: () =>
      import('../layouts/main-layout/main-layout').then((m) => m.MainLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('../layouts/navbar-layout/navbar-layout').then((m) => m.NavbarLayout),
        children: [
          { path: '', redirectTo: 'home', pathMatch: 'full' },
          {
            path: 'home',
            loadComponent: () =>
              import('../pages/home/home').then((m) => m.Home),
          },
          {
            path: 'properties/:id/groups',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/reservation-cycle/property-groups/property-groups').then((m) => m.PropertyGroups),
          },
          {
            path: 'properties/:id/invest',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/reservation-cycle/investment-calculator/investment-calculator').then((m) => m.InvestmentCalculator),
          },
          {
            path: 'properties/:id/invest-rules',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/reservation-cycle/invest-rules/invest-rules').then((m) => m.InvestRules),
          },
          {
            path: 'properties/:id/payment-conditions',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/reservation-cycle/payment-conditions/payment-conditions').then((m) => m.PaymentConditions),
          },
          {
            path: 'properties/:id/community-invite',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/reservation-cycle/community-invite/community-invite').then((m) => m.CommunityInvite),
          },
          {
            path: 'properties/:id/checkout',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/reservation-cycle/checkout/checkout').then((m) => m.Checkout),
          },
          {
            path: 'brokers/:id',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/brokers/broker-profile/broker-profile').then((m) => m.BrokerProfile),
          },
          {
            path: 'deals',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/deals/deals').then((m) => m.Deals),
          },
          {
            path: 'deals/:groupId',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/deals/deal-chat/deal-chat').then((m) => m.DealChat),
          },
          {
            path: 'deals/:groupId/payment-request',
            canActivate: [authGuard],
            loadComponent: () =>
              import(
                '../pages/chat/payment-request/ask-for-payment/ask-for-payment'
              ).then((m) => m.AskForPaymentPage),
          },
          {
            path: 'deals/:groupId/payment-request/edit',
            canActivate: [authGuard],
            loadComponent: () =>
              import(
                '../pages/chat/payment-request/ask-for-payment/ask-for-payment'
              ).then((m) => m.AskForPaymentPage),
          },
          {
            path: 'deals/:groupId/payment-request/review',
            canActivate: [authGuard],
            loadComponent: () =>
              import(
                '../pages/chat/payment-request/review-invoices/review-invoices'
              ).then((m) => m.ReviewInvoicesPage),
          },
          {
            path: 'deals/:groupId/payment-request/track',
            canActivate: [authGuard],
            loadComponent: () =>
              import(
                '../pages/chat/payment-request/track-invoices/track-invoices'
              ).then((m) => m.TrackInvoicesPage),
          },
          {
            path: 'properties/:id/listing',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/properties/listing-details/listing-details').then((m) => m.ListingDetails),
          },
          {
            path: 'properties/:id/dld-details',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/properties/dld-property-details/dld-property-details').then((m) => m.DldPropertyDetails),
          },
          {
            path: 'properties/add',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/properties/add-property/add-property').then((m) => m.AddPropertyComponent),
          },
          {
            path: 'properties/:id/proposals',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/properties/property-proposals/property-proposals').then((m) => m.PropertyProposals),
          },
          {
            path: 'properties/:id/list-with-broker',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/properties/list-with-broker/list-with-broker').then((m) => m.ListWithBroker),
          },
          {
            path: 'property-details',
            loadComponent: () =>
              import('../pages/property-view/property-view').then((m) => m.PropertyView),
          },
          {
            path: 'properties/:id',
            loadComponent: () =>
              import('../pages/property-view/property-view').then((m) => m.PropertyView),
          },
          {
            path: 'recommended-properties',
            loadComponent: () =>
              import('../pages/recommended-properties/recommended-properties-page').then(
                (m) => m.RecommendedPropertiesPage,
              ),
          },
          {
            path: 'invest',
            loadComponent: () =>
              import('../pages/invest/invest').then((m) => m.Invest),
          },
          {
            path: 'properties',
            loadComponent: () =>
              import('../pages/properties/properties').then((m) => m.Properties),
          },
          {
            path: 'events/:id',
            loadComponent: () =>
              import('../pages/events/event-details/event-details').then((m) => m.EventDetails),
          },
          {
            path: 'events',
            loadComponent: () =>
              import('../pages/events/events').then((m) => m.Events),
          },
          {
            path: 'reels/:id',
            loadComponent: () =>
              import('../pages/reels/reels').then((m) => m.Reels),
          },
          {
            path: 'reels',
            loadComponent: () =>
              import('../pages/reels/reels').then((m) => m.Reels),
          },
          {
            path: 'agency/:id',
            loadComponent: () =>
              import('../pages/agencies/agency-details/agency-details').then((m) => m.AgencyDetails),
          },
          {
            path: 'developers/:id',
            loadComponent: () =>
              import('../pages/developers/developer-details/developer-details').then((m) => m.DeveloperDetails),
          },
          {
            path: 'developers',
            loadComponent: () =>
              import('../pages/developers/developers').then((m) => m.Developers),
          },
          {
            path: 'saved',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/saved/saved').then((m) => m.Saved),
          },
          {
            path: 'projects/:id',
            loadComponent: () =>
              import('../pages/projects/project-details/project-details').then((m) => m.ProjectDetails),
          },
          {
            path: 'projects',
            loadComponent: () =>
              import('../pages/projects/projects').then((m) => m.Projects),
          },
          {
            path: 'map',
            loadComponent: () =>
              import('../pages/map/map').then((m) => m.MapPage),
          },
          {
            path: 'profile',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/profile/profile').then((m) => m.Profile),
          },
          {
            path: 'complete-profile',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/complete-profile/complete-profile').then((m) => m.CompleteProfile),
          },
          {
            path: 'investment-groups',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/communities/communities').then((m) => m.Communities),
          },
          {
            path: 'investment-groups/create',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/communities/create-community/create-community').then((m) => m.CreateCommunity),
          },
          {
            path: 'investment-groups/matched-groups',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/communities/matched-communities/matched-communities').then((m) => m.MatchedCommunities),
          },
          {
            path: 'investment-groups/matched-people',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/communities/matched-people/matched-people').then((m) => m.MatchedPeople),
          },
          {
            path: 'investment-groups/detail/:id',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/communities/community-detail/community-detail').then((m) => m.CommunityDetail),
          },
          {
            path: 'investment-groups/member/:id',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/communities/member-profile/member-profile').then((m) => m.MemberProfile),
          },
          // Legacy "communities" URLs — keep push notifications and bookmarks working.
          { path: 'communities', redirectTo: 'investment-groups', pathMatch: 'full' },
          { path: 'communities/create', redirectTo: 'investment-groups/create' },
          { path: 'communities/matched-communities', redirectTo: 'investment-groups/matched-groups' },
          { path: 'communities/matched-people', redirectTo: 'investment-groups/matched-people' },
          { path: 'communities/detail/:id', redirectTo: 'investment-groups/detail/:id' },
          { path: 'communities/member/:id', redirectTo: 'investment-groups/member/:id' },
          {
            path: 'interested-investors',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/communities/interested-investors/interested-investors').then((m) => m.InterestedInvestors),
          },
          {
            path: 'notifications',
            canActivate: [authGuard],
            loadComponent: () =>
              import('../pages/notifications/notifications').then((m) => m.NotificationsPage),
          },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'main',
  },
  {
    path: '',
    redirectTo: 'main',
    pathMatch: 'full',
  },
];
